import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { InventoryDisplayMode } from "@src/utils/inventoryDisplayMode";

export interface ApplyEventFiltersOptions {
	includeInactiveEvents?: boolean;
	inventoryDisplayMode?: InventoryDisplayMode;
	replace?: boolean;
}

type ApplyEventFiltersHandler = (options?: ApplyEventFiltersOptions) => void;

interface EventFiltersContextValue {
	applyCurrentEventFilters: (options?: ApplyEventFiltersOptions) => void;
	registerApplyCurrentEventFilters: (handler: ApplyEventFiltersHandler | null) => void;
}

const noop = () => undefined;

const EventFiltersContext = createContext<EventFiltersContextValue>({
	applyCurrentEventFilters: noop,
	registerApplyCurrentEventFilters: noop,
});

export const EventFiltersProvider = ({ children }: { children: React.ReactNode }) => {
	const handlerRef = useRef<ApplyEventFiltersHandler | null>(null);

	const registerApplyCurrentEventFilters = useCallback((handler: ApplyEventFiltersHandler | null) => {
		handlerRef.current = handler;
	}, []);

	const applyCurrentEventFilters = useCallback((options?: ApplyEventFiltersOptions) => {
		handlerRef.current?.(options);
	}, []);

	const value = useMemo(
		() => ({
			applyCurrentEventFilters,
			registerApplyCurrentEventFilters,
		}),
		[applyCurrentEventFilters, registerApplyCurrentEventFilters],
	);

	return <EventFiltersContext.Provider value={value}>{children}</EventFiltersContext.Provider>;
};

export const useEventFilters = () => useContext(EventFiltersContext);
